import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Loading from 'src/components/loading/Loading';
import WidgetPanel from 'src/components/dashboard/common/WidgetPanel';
import Paragraph from 'src/design/components/bodyText/Paragraph';
import Hyperlink from 'src/design/components/hyperlink/Hyperlink';
import PrimaryButton from 'src/design/components/button/PrimaryButton';
import SecondaryButton from 'src/design/components/button/SecondaryButton';
import { Heading } from 'src/design/styleguide/Headings';
import Spacing from 'src/design/styleguide/spacing/Spacing';
import AdvisorService from 'scripts/services/AdvisorService';
import { errorAlert, successAlert } from 'scripts/redux/actions/alerts';
import { showModal as showModalAction } from 'src/components/modals/modalActions';
import history from 'src/browser/history';
import { advisorClientDetailUrl, advisorClientsUrl } from 'src/utils/pageUrls';
import AdvisorClientDetailView from './AdvisorClientDetailView';
import { DETAIL_TABS } from './clientDetail/navigation';
import { normalizeAdvisorClientDetail } from './advisorClientDetailData';
import { getAdvisorErrorMessage as getErrorMessage } from './advisorErrorMessage';

const ErrorPanel = styled(WidgetPanel)`
  max-width: 1160px;
  margin: 0 auto;
`;

const ConfirmationModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ConfirmationButtons = styled.div`
  display: flex;
`;

class RevokeConnectionConfirmationModal extends Component {
  static propTypes = {
    clientName: PropTypes.string.isRequired,
    closeThisModal: PropTypes.func,
    onConfirm: PropTypes.func.isRequired,
  };

  state = { submitting: false };

  unmounted = false;

  componentWillUnmount() {
    this.unmounted = true;
  }

  confirm = async () => {
    this.setState({ submitting: true });
    const successful = await this.props.onConfirm();
    if (this.unmounted) return;

    if (successful) {
      this.props.closeThisModal();
      return;
    }
    this.setState({ submitting: false });
  };

  render() {
    const { clientName, closeThisModal } = this.props;
    const { submitting } = this.state;
    return (
      <ConfirmationModalContainer>
        <Heading as="p">Revoke connection with {clientName}?</Heading>
        <Spacing top="x-large">
          <Paragraph align="center">
            You will no longer be able to access this client&apos;s account and
            investment information.
          </Paragraph>
        </Spacing>
        <Spacing top="5x-large">
          <ConfirmationButtons>
            <SecondaryButton
              color={SecondaryButton.colors.PRIMARY}
              disabled={submitting}
              onClick={closeThisModal}
            >
              Cancel
            </SecondaryButton>
            <Spacing left="x-small">
              <PrimaryButton
                color={PrimaryButton.colors.SECONDARY}
                disabled={submitting}
                onClick={this.confirm}
              >
                {submitting ? 'Revoking...' : 'Revoke connection'}
              </PrimaryButton>
            </Spacing>
          </ConfirmationButtons>
        </Spacing>
      </ConfirmationModalContainer>
    );
  }
}

const DOCUMENT_ACTIONS = {
  portfolio: (clientId) => AdvisorService.downloadPortfolioStatement(clientId),
  'wholesale-certificate': (clientId, year, options) =>
    AdvisorService.downloadWholesaleCertificate(clientId, options),
  'annual-pdf': (clientId, year) =>
    AdvisorService.downloadAnnualStatementPdf(clientId, year),
  'annual-csv': (clientId, year) =>
    AdvisorService.downloadAnnualStatementCsv(clientId, year),
  'tax-report': (clientId, year, options) =>
    AdvisorService.downloadTaxReport(clientId, year, options),
};

@connect(null, {
  errorAlert,
  successAlert,
  showModal: showModalAction,
})
export default class AdvisorClientDetail extends Component {
  static propTypes = {
    errorAlert: PropTypes.func.isRequired,
    successAlert: PropTypes.func.isRequired,
    showModal: PropTypes.func.isRequired,
    children: PropTypes.element.isRequired,
    params: PropTypes.shape({
      clientId: PropTypes.string.isRequired,
    }).isRequired,
  };

  state = {
    loading: true,
    clientId: null,
    client: null,
    detail: null,
    orders: null,
    loadError: null,
    downloadingDocumentKey: null,
  };

  loadRequestId = 0;

  unmounted = false;

  componentDidMount() {
    this.unmounted = false;
    this.loadClient();
  }

  componentDidUpdate(previousProps) {
    if (previousProps.params.clientId !== this.props.params.clientId) {
      this.loadClient();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.loadRequestId += 1;
  }

  loadClient = async () => {
    const clientId = this.props.params.clientId;
    const requestId = this.loadRequestId + 1;
    this.loadRequestId = requestId;
    this.setState({
      loading: true,
      clientId,
      client: null,
      detail: null,
      orders: null,
      loadError: null,
      downloadingDocumentKey: null,
    });

    const [clientResult, ordersResult] = await Promise.all([
      AdvisorService.getClient(clientId)
        .then((value) => ({ value }))
        .catch((error) => ({ error })),
      AdvisorService.getClientOrders(clientId)
        .then((value) => ({ value }))
        .catch((error) => ({ error })),
    ]);

    if (this.unmounted || requestId !== this.loadRequestId) return;

    if (clientResult.error) {
      const message = getErrorMessage(
        clientResult.error,
        'Unable to load adviser client.',
      );
      this.props.errorAlert(message);
      this.setState({
        loading: false,
        loadError: message,
      });
      return;
    }

    if (ordersResult.error) {
      this.props.errorAlert(
        getErrorMessage(
          ordersResult.error,
          'Pending orders could not be loaded for this client.',
        ),
      );
    }

    this.setState({
      loading: false,
      client: clientResult.value,
      detail: clientResult.value
        ? normalizeAdvisorClientDetail(clientResult.value, ordersResult.value)
        : null,
      orders: Array.isArray(ordersResult.value) ? ordersResult.value : null,
    });
  };

  downloadDocument = async (documentKey, year, options) => {
    const downloadAction = DOCUMENT_ACTIONS[documentKey];
    if (
      !downloadAction ||
      this.unmounted ||
      this.state.downloadingDocumentKey
    ) {
      return;
    }
    const requestId = this.loadRequestId;
    const clientId = this.props.params.clientId;

    this.setState({
      downloadingDocumentKey: year ? `${documentKey}:${year}` : documentKey,
    });

    try {
      await downloadAction(clientId, year, options);
    } catch (error) {
      if (this.unmounted || requestId !== this.loadRequestId) return;

      this.props.errorAlert(
        getErrorMessage(
          error,
          'Unable to download this adviser client document.',
        ),
      );
    } finally {
      if (!this.unmounted && requestId === this.loadRequestId) {
        this.setState({ downloadingDocumentKey: null });
      }
    }
  };

  revokeConnection = async (
    clientId = this.props.params.clientId,
    requestId = this.loadRequestId,
  ) => {
    if (
      this.unmounted ||
      requestId !== this.loadRequestId ||
      clientId !== this.props.params.clientId
    ) {
      return false;
    }
    try {
      await AdvisorService.revokeConnection(clientId);
      if (this.unmounted || requestId !== this.loadRequestId) return false;

      this.props.successAlert('Client connection revoked.');
      history.push(advisorClientsUrl());
      return true;
    } catch (error) {
      if (this.unmounted || requestId !== this.loadRequestId) return false;

      this.props.errorAlert(
        getErrorMessage(error, 'Unable to revoke this client connection.'),
      );
      return false;
    }
  };

  showRevokeConfirmation = (clientName) => {
    const clientId = this.props.params.clientId;
    const requestId = this.loadRequestId;
    this.props.showModal({
      content: (
        <RevokeConnectionConfirmationModal
          clientName={clientName}
          onConfirm={() => this.revokeConnection(clientId, requestId)}
        />
      ),
      testRef: 'advisor-client-revoke-confirmation-modal',
    });
  };

  selectTab = (tab) => {
    if (
      DETAIL_TABS.some(({ id }) => id === tab) &&
      tab !== this.props.children.props.route.path
    ) {
      history.push(advisorClientDetailUrl(this.props.params.clientId, tab));
    }
  };

  render() {
    const { loading, client, detail, loadError, downloadingDocumentKey } =
      this.state;

    if (loading || this.state.clientId !== this.props.params.clientId) {
      return <Loading />;
    }

    if (loadError || !client) {
      return (
        <ErrorPanel title="Adviser Client Detail">
          <Paragraph>{loadError || 'Unable to load client details.'}</Paragraph>
          <Paragraph>
            <Hyperlink href={advisorClientsUrl()}>
              Back to adviser clients
            </Hyperlink>
          </Paragraph>
        </ErrorPanel>
      );
    }

    return (
      <AdvisorClientDetailView
        clientId={this.props.params.clientId}
        detail={detail}
        activeTab={this.props.children.props.route.path}
        downloadingDocumentKey={downloadingDocumentKey}
        onDownloadDocument={this.downloadDocument}
        onRevokeConnection={() =>
          this.showRevokeConfirmation(detail.identity.name)
        }
        onTabSelect={this.selectTab}
      >
        {this.props.children}
      </AdvisorClientDetailView>
    );
  }
}
